import { HttpErrorResponse, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { EnvironmentService } from '@pinnakl/core/environment';
import { HTTP_SERVICE_URL } from '@pinnakl/shared/constants';
import {
  AmplitudeCRMEventTypes,
  AmplitudeHttpErrorEventProperties,
  AmplitudePOEMSEventTypes,
  AppNames,
} from '@pinnakl/shared/types';
import { AmplitudeEventsCollector } from '@pinnakl/shared/util-external-services';
import { getErrorMessage, getErrorObject } from '@pinnakl/shared/util-helpers';
import { SessionExpirationService } from '@pinnakl/shared/util-providers';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take, timeout } from 'rxjs/operators';
import { AuthService } from './auth.service';

const AUTH_SERVER_PATH_PREFIXES = ['/connect/', '/.well-known/', '/account/'];

export function httpRequestsAuthInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<any> {
  const authService = inject(AuthService);
  const httpServiceUrl = inject<string>(HTTP_SERVICE_URL);
  const url = request.url.includes('http') ? request.url : httpServiceUrl.concat(request.url);
  const origin = url ? new URL(url)?.origin : '';

  const is3rdPartyRequest = (): boolean => {
    return ['https://api.ipdata.co'].includes(origin);
  };

  const isApiCoreService = (): boolean => {
    return origin.includes('apicore') || origin.includes('51490');
  };

  const isApiPoemsService = (): boolean => {
    return origin.includes('api.dev.pinnakl');
  };

  const isFileService = (): boolean => {
    return (
      origin.includes('backendmiscservice') ||
      origin.includes('externalservices') ||
      origin.includes('localhost:53591') ||
      origin.includes('external-services') ||
      origin.includes('documents')
    );
  };

  const isEmailsCollectorService = (): boolean => {
    return origin.includes('emailscollector') || origin.includes('localhost:5151');
  };

  const isPinnaklService = (): boolean => {
    return origin.includes('pinnakl.com');
  };

  const isAuthServerRequest = (): boolean => {
    try {
      const { pathname } = new URL(url);
      return AUTH_SERVER_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix));
    } catch {
      return false;
    }
  };

  const isLongPollingRequest = (): boolean => {
    const longPollingEndpoints = ['v3/entities/realtime_portfolio', 'v4/entities/orders'];
    return longPollingEndpoints.some(e => url.includes(e));
  };

  const formatToken = (token: string): string => {
    return isApiCoreService() ||
      isFileService() ||
      isEmailsCollectorService() ||
      isApiPoemsService() ||
      isPinnaklService()
      ? 'Bearer ' + token
      : token;
  };

  const amplitudeEventsCollector = inject(AmplitudeEventsCollector);
  const sessionExpirationService = inject(SessionExpirationService);
  const handleError = (err: HttpErrorResponse): Observable<any> => {
    console.error(err.message);
    const appName = environmentService.get<AppNames>('appName');
    const isCrm = appName === AppNames.CRM;
    const isPoems = appName === AppNames.PLATFORM_WEB;
    const isInvestorPortal = appName === AppNames.INVESTOR_PORTAL;
    if (isCrm || isPoems || isInvestorPortal) {
      amplitudeEventsCollector.track(isCrm ? AmplitudeCRMEventTypes.HttpError : AmplitudePOEMSEventTypes.HttpError, <
        AmplitudeHttpErrorEventProperties
      >{
        location: location.href,
        url,
        parsed_error: getErrorMessage('Can not extract error message.', err),
        error: getErrorObject(err),
      });
    }
    return throwError(() => err);
  };

  const expireSession = (): void => {
    sessionExpirationService.markExpired();
    if (isLongPollingRequest()) {
      authService.forceLogout();
    } else {
      authService.localLogout();
    }
  };

  const isAccessError = (err: unknown): boolean =>
    err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403);

  const denyAppAccess = (err: HttpErrorResponse): Observable<unknown> => {
    authService.reportAppAccessDenied();
    return handleError(err);
  };

  const refreshAndRetry = (request: HttpRequest<unknown>, next: HttpHandlerFn, originalError: HttpErrorResponse) => {
    return authService.getRefreshedAccessToken().pipe(
      catchError(() => {
        expireSession();
        return handleError(originalError);
      }),
      switchMap(accessToken =>
        withRequestTimeout(
          next(request.clone({ headers: request.headers.set('Authorization', formatToken(accessToken)) })),
        ).pipe(
          catchError(err =>
            (isAccessError(err) ? authService.hasAppApiAccess() : of(true)).pipe(
              switchMap(hasAccess => {
                if (!hasAccess) return denyAppAccess(err);
                if (err instanceof HttpErrorResponse && err.status === 401) expireSession();
                return handleError(err);
              }),
            ),
          ),
        ),
      ),
    );
  };

  const handle401Error = (request: HttpRequest<any>, next: HttpHandlerFn, originalError: HttpErrorResponse) => {
    if (!authService.appAccessDenied()) return refreshAndRetry(request, next, originalError);
    return authService
      .hasAppApiAccess()
      .pipe(
        switchMap(hasAccess =>
          hasAccess ? refreshAndRetry(request, next, originalError) : denyAppAccess(originalError),
        ),
      );
  };

  const environmentService = inject(EnvironmentService);

  const requestTimeoutPeriod = environmentService.environment?.requestTimeoutPeriod;
  const canTimeOut = (): boolean =>
    !!requestTimeoutPeriod &&
    !isLongPollingRequest() &&
    !isFileService() &&
    !is3rdPartyRequest() &&
    !request.reportProgress &&
    (request.responseType ?? 'json') === 'json';
  const withRequestTimeout = <T>(source: Observable<T>): Observable<T> =>
    canTimeOut() ? source.pipe(timeout({ first: requestTimeoutPeriod })) : source;

  if (request.url.includes('maps.googleapis.com') || request.url.includes('/authorize')) {
    return next(request);
  }

  if (request.method === 'GET' && ['.json', '.svg', '.png', '.jpg'].some(end => request.url.endsWith(end))) {
    return next(request);
  }

  const withAuthHeaders = (token: string): HttpRequest<unknown> => {
    let updatedRequest = request.clone({ url });
    if (is3rdPartyRequest()) return updatedRequest;
    if (token && !request.headers.has('Authorization')) {
      updatedRequest = updatedRequest.clone({
        headers: updatedRequest.headers.set('Authorization', formatToken(token)),
      });
    }
    if (!updatedRequest.headers.has('Content-Type') && !isFileService()) {
      updatedRequest = updatedRequest.clone({
        headers: updatedRequest.headers.set('Content-Type', 'application/json'),
      });
    }
    return updatedRequest.clone({
      headers: updatedRequest.headers.set('Accept', 'application/json'),
    });
  };

  return authService.getAccessToken().pipe(
    take(1),
    switchMap(token => {
      const updatedRequest = withAuthHeaders(token);
      return withRequestTimeout(next(updatedRequest)).pipe(
        catchError(error => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            const authWellKnownEndPoints = authService.authWellKnownEndPoints;
            const { pathname, origin } = new URL(error.url ?? '');
            const isAuthUrl = Object.values(authWellKnownEndPoints).includes(origin + pathname);
            if (!is3rdPartyRequest() && !isAuthUrl && !isAuthServerRequest()) {
              console.error('Unauthorized', error.message, error.status);
              return handle401Error(updatedRequest, next, error);
            }
            return handleError(error);
          }
          if (
            error instanceof HttpErrorResponse &&
            error.status === 403 &&
            !is3rdPartyRequest() &&
            !isAuthServerRequest()
          ) {
            return authService
              .hasAppApiAccess()
              .pipe(switchMap(hasAccess => (hasAccess ? handleError(error) : denyAppAccess(error))));
          }
          return handleError(error);
        }),
      );
    }),
  );
}
